import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AureloLogo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, UserCog, Users, Calendar, GitBranch, Upload, Sparkles, Rocket, Check, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { key: "org", title: "Organization", icon: Building2 },
  { key: "role", title: "Your Role", icon: UserCog },
  { key: "team", title: "Invite Team", icon: Users },
  { key: "google", title: "Connect Google", icon: Calendar },
  { key: "pipeline", title: "Sales Pipeline", icon: GitBranch },
  { key: "import", title: "Import Contacts", icon: Upload },
  { key: "ai", title: "Configure AI", icon: Sparkles },
  { key: "launch", title: "Launch", icon: Rocket },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, tenant, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState(tenant?.name || "");
  const [role, setRole] = useState("Sales Manager");
  const S = STEPS[step];

  const finish = async () => {
    try { if (org) await api.put("/tenant", { name: org }); await api.post("/auth/onboard"); await refresh(); toast.success("Workspace ready!"); navigate("/app"); }
    catch { toast.error("Something went wrong"); }
  };
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish());

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex">
      <div className="hidden md:flex flex-col w-72 bg-[#0B102B] text-white p-6">
        <AureloLogo size={28} light />
        <div className="mt-10 space-y-1">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm ${i === step ? "bg-white/10" : i < step ? "text-slate-300" : "text-slate-500"}`}>
              <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${i < step ? "bg-[#10B981]" : i === step ? "aurelo-gradient" : "bg-white/10"}`}>
                {i < step ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
              </div>
              <span>{s.title}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg" data-testid="onboarding-stepper">
          <div className="md:hidden mb-6 h-1.5 rounded-full bg-slate-200"><div className="h-1.5 rounded-full aurelo-gradient" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
          <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm">
            <div className="h-12 w-12 rounded-xl aurelo-gradient flex items-center justify-center mb-5"><S.icon className="h-6 w-6 text-white" /></div>
            <div className="text-xs font-semibold text-[#7C3AED] uppercase tracking-wide">Step {step + 1} of {STEPS.length}</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">{S.title}</h1>

            <div className="mt-6 space-y-4">
              {S.key === "org" && <div><Label>Organization name</Label><Input value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1.5" placeholder="Acme Inc." /></div>}
              {S.key === "role" && (
                <div className="grid grid-cols-2 gap-2">
                  {["Sales Manager", "Sales Rep", "RevOps", "Marketing", "Customer Success", "Founder"].map((r) => (
                    <button key={r} onClick={() => setRole(r)} className={`rounded-xl border px-3 py-3 text-sm font-medium text-left ${role === r ? "border-[#7C3AED] bg-violet-50 text-[#7C3AED]" : "border-slate-200 hover:bg-slate-50"}`}>{r}</button>
                  ))}
                </div>
              )}
              {S.key === "team" && <p className="text-sm text-slate-500">Invite teammates later from Administration. You can skip this for now.</p>}
              {S.key === "google" && (
                <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                  <div><div className="font-medium text-sm">Google Workspace</div><div className="text-xs text-slate-500">Gmail, Calendar, Meet, Drive</div></div>
                  <Button variant="outline" onClick={() => toast("Connect from Settings → Integrations")}>Connect</Button>
                </div>
              )}
              {S.key === "pipeline" && <p className="text-sm text-slate-500">We've set up a default 9-stage sales pipeline. You can customize stages anytime in Settings.</p>}
              {S.key === "import" && <p className="text-sm text-slate-500">Import contacts via CSV or connect Google Contacts later. Your demo workspace already has sample data.</p>}
              {S.key === "ai" && <p className="text-sm text-slate-500">Aurelo AI is enabled with lead scoring, deal intelligence and the Copilot. You're all set.</p>}
              {S.key === "launch" && <div className="text-center py-4"><div className="text-lg font-semibold">You're ready to grow 🚀</div><p className="text-sm text-slate-500 mt-1">Your Aurelo revenue workspace is configured.</p></div>}
            </div>

            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" data-testid="onboarding-back-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
              <div className="flex gap-2">
                {step < STEPS.length - 1 && step > 1 && <Button variant="outline" onClick={next}>Skip</Button>}
                <Button data-testid="onboarding-next-button" className="aurelo-gradient text-white" onClick={next}>{step === STEPS.length - 1 ? "Launch Workspace" : "Continue"}<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
